import { FolderOpen } from 'lucide-react';
import type { RunTarget } from './RunOnSelector';

const QUICK_FILLS = ['~', '~/Code', '~/Projects', '~/Desktop'];

function storageKey(target: RunTarget): string {
  if (target.mode === 'host') return `aldan-wdirs-h-${target.runnerId}`;
  if (target.mode === 'ssh') return `aldan-wdirs-s-${target.sshHostId}`;
  return 'aldan-wdirs-sdk';
}

// eslint-disable-next-line react-refresh/only-export-components
export function saveRecentWorkDir(target: RunTarget, path: string): void {
  if (!path.trim()) return;
  try {
    const key = storageKey(target);
    const existing: string[] = JSON.parse(localStorage.getItem(key) ?? '[]');
    const updated = [path, ...existing.filter((p) => p !== path)].slice(0, 5);
    localStorage.setItem(key, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

function getRecentWorkDirs(target: RunTarget): string[] {
  try {
    return JSON.parse(localStorage.getItem(storageKey(target)) ?? '[]');
  } catch {
    return [];
  }
}

function getContextLabel(target: RunTarget): string {
  if (target.mode === 'host') return `Working directory on ${target.runnerName}`;
  if (target.mode === 'ssh') return `Working directory on ${target.sshName}`;
  return 'Working directory';
}

function getPlaceholder(target: RunTarget): string {
  if (target.mode === 'ssh') return '/home/user/myproject';
  if (target.mode === 'host') {
    if (target.platform === 'darwin') return '/Users/you/Code/myproject';
    if (target.platform === 'win32') return 'C:\\Users\\you\\Code\\myproject';
    return '/home/user/myproject';
  }
  return '/path/to/project (optional)';
}

interface WorkDirInputProps {
  value: string;
  onChange: (path: string) => void;
  target: RunTarget;
}

export default function WorkDirInput({ value, onChange, target }: WorkDirInputProps) {
  const recents = getRecentWorkDirs(target).filter((r) => !QUICK_FILLS.includes(r));

  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <FolderOpen className="h-3.5 w-3.5 text-slate-400" />
        {getContextLabel(target)}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={getPlaceholder(target)}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
      />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {QUICK_FILLS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-500 hover:border-slate-300 hover:text-slate-700"
          >
            {p}
          </button>
        ))}
        {recents.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => onChange(r)}
            className="max-w-[180px] truncate rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-500 hover:border-slate-300 hover:text-slate-700"
            title={r}
          >
            {r.length > 28 ? `…${r.slice(-25)}` : r}
          </button>
        ))}
      </div>
    </div>
  );
}
